import React from 'react';
import {Image, Page, Text, View} from '@react-pdf/renderer';
import {IntlShape} from 'react-intl';
import {companyLogoSrc} from '../../../../directApi';
import {buildStyles} from './quotePdfStyles';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';

type QuotePageProps = {
	data: QuoteDocumentData;
	template: QuoteTemplate;
	intl: IntlShape;
	/** The cover drops the running head and the band's bottom border. */
	variant?: 'cover';
	children: React.ReactNode;
};

/**
 * The A4 frame every quote page shares: a fixed top band (small logo, "Client · Project",
 * "Confidential") and a fixed footer (company name, "N of T" with real page numbers).
 */
const QuotePage = ({data, template, intl, variant, children}: QuotePageProps): JSX.Element => {
	const styles = buildStyles(template.font);
	const logoSrc = data.companyLogoId ? companyLogoSrc(data.companyLogoId) : null;
	const runningHead = data.clientName ? `${data.clientName} · ${data.projectName}` : data.projectName;

	return (
		<Page size="A4" style={styles.page}>
			<View fixed style={variant === 'cover' ? styles.bandCover : styles.band}>
				{variant !== 'cover' && (
					<View style={styles.bandRun}>
						{logoSrc ? (
							<Image src={{uri: logoSrc, credentials: 'include'}} style={styles.bandLogoImage} />
						) : (
							<Text style={styles.bandLogoWord}>{data.companyName}</Text>
						)}
						<Text style={styles.bandRunText}>{runningHead}</Text>
					</View>
				)}
				<Text style={styles.bandConfidential}>{intl.formatMessage({id: 'quote_designer.pdf.confidential'})}</Text>
			</View>
			<View style={styles.content}>{children}</View>
			<View fixed style={styles.foot}>
				<Text style={styles.footText}>{data.companyName}</Text>
				<Text
					style={styles.footText}
					render={({pageNumber, totalPages}) =>
						intl.formatMessage({id: 'quote_designer.pdf.page_of'}, {page: pageNumber, total: totalPages})
					}
				/>
			</View>
		</Page>
	);
};

export default QuotePage;
